import {
    List,
    Datagrid,
    DateField,
    TextField,
    useRecordContext,
    FunctionField,
} from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { Box, Chip, Divider, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import 'moment/locale/pt-br';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CasinoIcon from '@mui/icons-material/Casino';
import { RoletaSegmentKind, RoletaSpinStatus } from '../../models/roleta';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';

const getCouponStatus = (coupon: any) => {
    if (coupon.refunded) return { label: 'Estornado', color: 'error' as const };
    if (coupon.redeemed) return { label: 'Utilizado', color: 'success' as const };
    if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) return { label: 'Expirado', color: 'warning' as const };
    return { label: 'Disponível', color: 'info' as const };
};

const getDiscountCouponStatus = (dc: any) => {
    if (dc.status === 'redeemed') return { label: 'Utilizado', color: 'success' as const };
    if (dc.status === 'expired') return { label: 'Expirado', color: 'warning' as const };
    return { label: 'Disponível', color: 'info' as const };
};

const getPredictionTicketStatus = (ticket: any) => {
    if (ticket.isWinner === true) return { label: 'Acertou', color: 'success' as const };
    if (ticket.submitted) return { label: 'Palpite enviado', color: 'info' as const };
    return { label: 'Aguardando palpite', color: 'warning' as const };
};

const getSpinTicketStatus = (spin: any) => {
    if (spin.status === RoletaSpinStatus.Expired) return { label: 'Venceu sem girar', color: 'default' as const };
    if (spin.status !== RoletaSpinStatus.Spun) return { label: 'Aguardando giro', color: 'warning' as const };
    if (spin.segmentKind === RoletaSegmentKind.Prize) return { label: 'Girou e ganhou', color: 'success' as const };
    if (spin.segmentKind === RoletaSegmentKind.ExtraSpin) return { label: 'Ganhou outro giro', color: 'info' as const };
    return { label: 'Passou a vez', color: 'default' as const };
};

const describeSpinTicket = (spin: any): string => {
    if (spin.status === RoletaSpinStatus.Expired) return 'O giro venceu antes de o cliente usar';
    if (spin.status !== RoletaSpinStatus.Spun) return 'Aguardando o cliente girar';
    if (spin.segmentKind === RoletaSegmentKind.Prize) return `Prêmio: ${spin.prizeName || spin.segmentLabel || '—'}`;
    if (spin.segmentKind === RoletaSegmentKind.ExtraSpin) return 'A roda deu outro giro ao cliente';
    return 'Não foi dessa vez';
};

// Componente expandable que mostra as recompensas
const AchievementExpandableRow = () => {
    const record = useRecordContext();

    if (!record) return null;

    const hasCoupons = record.coupons && record.coupons.length > 0;
    const hasDiscountCoupons = record.discountCoupons && record.discountCoupons.length > 0;
    const hasPredictionTickets = record.predictionTickets && record.predictionTickets.length > 0;
    // Número da Sorte não gera cupom: a recompensa é a linha em drawing_lucky_number.
    // Sem contar aqui, a conquista cai no "Nenhuma recompensa disponível".
    const hasLuckyNumbers = record.luckyNumbers && record.luckyNumbers.length > 0;
    // Giro na Roleta também não gera cupom: a recompensa é a linha em roleta_spin.
    const hasSpinTickets = record.spinTickets && record.spinTickets.length > 0;

    if (!hasCoupons && !hasDiscountCoupons && !hasPredictionTickets && !hasLuckyNumbers && !hasSpinTickets) {
        return (
            <Box sx={{ p: 3, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Nenhuma recompensa disponível
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
            <Paper elevation={2} sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <LocalOfferIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Recompensas</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    {/* Cupons Regulares */}
                    {hasCoupons && record.coupons.map((coupon: any, index: number) => {
                        const status = getCouponStatus(coupon);
                        const couponNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={coupon.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#e3f2fd',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CardGiftcardIcon sx={{ fontSize: 28, color: '#1976d2' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Cupom"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={`#${couponNumber}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        color: '#666',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={status.label}
                                                    size="small"
                                                    color={status.color}
                                                />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {coupon.title || 'Cupom'}
                                            </Typography>
                                            {coupon.description && (
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    {coupon.description}
                                                </Typography>
                                            )}
                                            {coupon.barcode && (
                                                <Permission permission={PERMISSIONS.COUPONS_CODE}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Código: {coupon.barcode}
                                                    </Typography>
                                                </Permission>
                                            )}
                                            {coupon.expiresAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Validade: {moment(coupon.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Descontos de Combustível */}
                    {hasDiscountCoupons && record.discountCoupons.map((dc: any, index: number) => {
                        const status = getDiscountCouponStatus(dc);
                        const discountNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={dc.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#fff3e0',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <LocalGasStationIcon sx={{ fontSize: 28, color: '#f57c00' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Desconto Combustível"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#fff3e0',
                                                        color: '#f57c00',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={`#${discountNumber}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        color: '#666',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={status.label}
                                                    size="small"
                                                    color={status.color}
                                                />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                Desconto Combustível
                                            </Typography>
                                            {dc.expiresAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Validade: {moment(dc.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                            {dc.fuelDiscounts && dc.fuelDiscounts.length > 0 && (
                                                <Box mt={1}>
                                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                        Combustíveis:
                                                    </Typography>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {dc.fuelDiscounts.map((fd: any, idx: number) => (
                                                            <Chip
                                                                key={idx}
                                                                label={fd.fuelName || `Combustível ${idx + 1}`}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Direito ao Palpite (Bolão) */}
                    {hasPredictionTickets && record.predictionTickets.map((ticket: any, index: number) => {
                        const status = getPredictionTicketStatus(ticket);
                        const ticketNumber = String(index + 1).padStart(2, '0');
                        const matchLabel = ticket.bolaoName
                            || [ticket.homeTeamName, ticket.awayTeamName].filter(Boolean).join(' x ')
                            || 'Bolão';
                        const hasGuess = ticket.scoreHome !== null && ticket.scoreHome !== undefined
                            && ticket.scoreAway !== null && ticket.scoreAway !== undefined;

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={ticket.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#e8f5e9',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <SportsSoccerIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Direito ao Palpite"
                                                    size="small"
                                                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                                />
                                                <Chip
                                                    label={`#${ticketNumber}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#666', fontWeight: 600 }}
                                                />
                                                <Chip label={status.label} size="small" color={status.color} />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {matchLabel}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {hasGuess
                                                    ? `Palpite: ${ticket.scoreHome} x ${ticket.scoreAway}`
                                                    : 'Aguardando palpite do cliente'}
                                            </Typography>
                                            {ticket.scheduledAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Jogo: {moment(ticket.scheduledAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Giro na Roleta */}
                    {hasSpinTickets && record.spinTickets.map((spin: any, index: number) => {
                        const status = getSpinTicketStatus(spin);
                        const spinNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={spin.id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: '#ede9fe',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CasinoIcon sx={{ fontSize: 28, color: '#7869DF' }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label="Giro na Roleta"
                                                    size="small"
                                                    sx={{ bgcolor: '#ede9fe', color: '#7869DF', fontWeight: 600 }}
                                                />
                                                <Chip
                                                    label={`#${spinNumber}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#666', fontWeight: 600 }}
                                                />
                                                <Chip label={status.label} size="small" color={status.color} />
                                            </Stack>
                                            <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                {spin.roletaName || 'Roleta da Sorte'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {describeSpinTicket(spin)}
                                            </Typography>
                                            {spin.expiresAt && spin.status !== RoletaSpinStatus.Spun && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Vence em: {moment(spin.expiresAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                            {spin.spunAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Girou em: {moment(spin.spunAt).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Número da Sorte (Sorteio) */}
                    {hasLuckyNumbers && record.luckyNumbers.map((luckyNumber: any) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={luckyNumber.id}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: '#fafafa',
                                    height: '100%',
                                }}
                            >
                                <Box display="flex" alignItems="flex-start" gap={2}>
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            bgcolor: '#ede7f6',
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <ConfirmationNumberIcon sx={{ fontSize: 28, color: '#5e35b1' }} />
                                    </Box>
                                    <Box flex={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                            <Chip
                                                label="Número da Sorte"
                                                size="small"
                                                sx={{ bgcolor: '#ede7f6', color: '#5e35b1', fontWeight: 600 }}
                                            />
                                            {luckyNumber.isWinner && (
                                                <Chip label="Premiado" size="small" color="success" />
                                            )}
                                        </Stack>
                                        <Typography variant="h6" fontWeight={700} mb={0.5}>
                                            {luckyNumber.number}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {luckyNumber.drawingName || 'Sorteio'}
                                        </Typography>
                                        {luckyNumber.drawingDate && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Sorteio: {moment(luckyNumber.drawingDate).format('DD/MM/YYYY HH:mm')}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );
};

const AchievementsFilter = [
    <DateTimeInputUTC source="startDate" label="Data Inicial" alwaysOn />,
    <DateTimeInputUTC source="endDate" label="Data Final" alwaysOn />,
];

const Empty = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>🏆</Typography>
        <Typography variant="body1" color="text.secondary">
            Nenhuma conquista registrada para este cliente
        </Typography>
    </Box>
);

const CustomerAchievementsList = () => {
    const record = useRecordContext();
    const theme = useTheme();

    if (!record) return null;

    // Filtro padrão: último mês às 00:00 até hoje às 23:59
    const endDate = moment().endOf('day').format('YYYY-MM-DDTHH:mm');
    const startDate = moment().subtract(1, 'month').startOf('day').format('YYYY-MM-DDTHH:mm');

    return (
        <List
            resource={`customers/${record.id}/achievements`}
            title=" "
            actions={false}
            filters={AchievementsFilter}
            filterDefaultValues={{ startDate, endDate }}
            sort={{ field: 'createdAt', order: 'DESC' }}
            perPage={25}
            empty={<Empty />}
        >
            <Datagrid
                bulkActionButtons={false}
                expand={<AchievementExpandableRow />}
            >
                <FunctionField
                    label="Código do Brinde"
                    render={(record: any) => (
                        record.giftCode ? (
                            <a
                                href={`#/gifts/${record.giftId}/show`}
                                style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}
                            >
                                {record.giftCode}
                            </a>
                        ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                        )
                    )}
                />
                <TextField source="giftName" label="Nome do Brinde" />
                <DateField source="createdAt" label="Data e Hora" showTime locales="pt-BR" />
            </Datagrid>
        </List>
    );
};

export default CustomerAchievementsList;
